import { setDatadogUser } from '@cartwhl/tracking/datadog';
import type { StytchHeadlessClient } from '@stytch/vanilla-js/headless';
import { EXPORT_TYPES } from 'dynamo-daos/build/models/exports/exportRecord';
import { ulid } from 'ulid';
import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { getApiDomain } from '@cartwhl/common/api/api-utils';
import manageCartwheelSession from '@cartwhl/common/api/manageCartwheelSession';
import updateUserInfo from '@cartwhl/common/api/updateUserInfo';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as userStore from '@cartwhl/common/stores/userStore';

import {
  ANONYMOUS_USER_ID,
  NEW_USER_REDIRECT_TO,
  appendClientQueryParam,
  createAnomymousUserCookieIfNeeded,
  getCookieSuffix,
  getCurrentClientSlug,
  getDashboardUrl,
  getStytchToken,
} from '../utils';
import handleGenericErrors from '../utils/handleGenericErrors';
import {
  clearPendingSignupInfo,
  readPendingSignupInfo,
} from '../utils/pendingSignupInfoStorage';

const PRODUCT_FOCUS_TO_EXPORT_TYPE: Record<string, EXPORT_TYPES> = {
  MAYA: EXPORT_TYPES.MA,
  BLENDER: EXPORT_TYPES.FBX_BLENDER,
  UNREAL: EXPORT_TYPES.FBX_UNREAL,
  UNITY: EXPORT_TYPES.FBX_UNITY,
  OTHER: EXPORT_TYPES.FBX_UNREAL,
};

// https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const StytchMessages = {
  bad_domain_for_stytch_sdk: 'System error. Please notify support.',
  no_match_for_provided_magic_link_url: 'Something wen’t wrong',
  duplicate_email:
    'A user with the specified email already exists. Try logging in.',
  breached_password:
    'Password appears in a list of breached passwords. Please choose a different one.',
  weak_password: 'Password doesn’t meet our strength requirements.',
  reset_password: 'Please reset your password before logging in again.',
  no_user_password: 'Try signing in using Google login.',
};
export type StytchAPIErrorType = keyof typeof StytchMessages;

export type StytchAPIError = {
  error_type: StytchAPIErrorType;
  status_code: number;
  request_id: string;
  error_message: string;
  error_url: string;
};

export type AuthStoreState = {
  authStatus: 'anonymous' | 'user';
  userID?: string;
  /** Metrics working-session ID (ses-{ulid}); sliding 1-hour TTL in localStorage. */
  workingSessionID?: string;
};

const SESSION_DURATION_MINUTES = 60 * 24 * 7;
/** TTL for the metrics workingSessionID stored in localStorage. */
const WORKING_SESSION_TTL_MS = 30 * 60 * 1000;
const WORKING_SESSION_STORAGE_KEY = 'cartwheelWorkingSession';

type StoredWorkingSession = {
  workingSessionID: string;
  lastActivityAt: number;
};

let stytchClient: StytchHeadlessClient | undefined;
let stytchClientImport:
  | { generation: number; promise: Promise<StytchHeadlessClient> }
  | undefined;
let stytchClientGeneration = 0;
let authActionEpoch = 0;
let authSessionOperationTail: Promise<void> | undefined;

class AuthActionCancelledError extends Error {}

async function getStytchClient(): Promise<StytchHeadlessClient> {
  if (stytchClient) return stytchClient;

  const generation = stytchClientGeneration;
  if (!stytchClientImport || stytchClientImport.generation !== generation) {
    const promise = import('@stytch/vanilla-js/headless')
      .then(
        ({ StytchHeadlessClient }) =>
          new StytchHeadlessClient(getStytchToken()),
      )
      .catch((error) => {
        if (stytchClientImport?.generation === generation) {
          stytchClientImport = undefined;
        }
        throw error;
      });
    stytchClientImport = { generation, promise };
  }

  const client = await stytchClientImport.promise;
  if (generation !== stytchClientGeneration)
    throw new AuthActionCancelledError();
  stytchClient = client;
  return client;
}

function resetStytchClient() {
  stytchClientGeneration += 1;
  stytchClient = undefined;
  stytchClientImport = undefined;
}

function isCurrentAuthAction(epoch: number) {
  return epoch === authActionEpoch;
}

function isCancelledAuthAction(error: unknown) {
  return error instanceof AuthActionCancelledError;
}

/**
 * Serializes only operations that can establish or destroy the Cartwheel
 * session. In particular, logout must run after an already-sent passwordLogin
 * response (and its local initialization) so logout is the final mutation.
 */
function runAuthSessionOperation<T>(operation: () => Promise<T>): Promise<T> {
  const result = authSessionOperationTail
    ? authSessionOperationTail.then(operation)
    : operation();
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  authSessionOperationTail = tail;
  void tail.then(() => {
    if (authSessionOperationTail === tail) authSessionOperationTail = undefined;
  });
  return result;
}

export const state = proxy<AuthStoreState>({
  authStatus: getInitialStatus(),
  userID: undefined,
  workingSessionID: undefined,
});

if (
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  '__REDUX_DEVTOOLS_EXTENSION__' in window
) {
  devtools(state, { name: 'Auth', enabled: true });
}

//// Actions:

export function init() {
  return runAuthSessionOperation(initializeAuthState);
}

async function initializeAuthState() {
  createAnomymousUserCookieIfNeeded();
  state.userID = getUserIDFromCookie();
  state.workingSessionID = ensureWorkingSessionID();

  // If the user is anonymous, we don't need to check the session
  if (state.userID === ANONYMOUS_USER_ID || window.self !== window.parent) {
    state.authStatus = 'anonymous';
    // check their localStorage for an anonymous user ID, create one and set it if it doesn't exist.
    // gets used so that we can deduplicate sessions for anonymous users in Datadog
    let anonymousUserID = localStorage.getItem('anonymousUserID');
    if (!anonymousUserID) {
      anonymousUserID = `anonymous-${crypto.randomUUID()}`;
      localStorage.setItem('anonymousUserID', anonymousUserID);
    }
    setDatadogUser({
      id: anonymousUserID,
      isAnonymous: true,
      email: '',
      name: '',
      planType: '',
    });
  } else {
    state.authStatus = 'user';
    await userStore.load(false);
    await flushPendingSignupInfo();
  }
}

async function flushPendingSignupInfo() {
  const pending = readPendingSignupInfo();
  if (!pending) {
    return;
  }

  const exportType = PRODUCT_FOCUS_TO_EXPORT_TYPE[pending.productFocus];

  const { data, error } = await updateUserInfo({
    industryFocus: pending.industryFocus,
    productFocus: pending.productFocus,
    ...(exportType ? { defaultExportSettings: { exportType } } : {}),
  });

  if (error) {
    console.error('Failed to save signup focus fields', error);
    return;
  }

  clearPendingSignupInfo();

  if (data?.userInfo) {
    userStore.state.userInfo = data.userInfo;
  }
}

/**
 * Returns the current metrics working-session ID, creating/renewing one if needed.
 * Touches lastActivityAt on each call (sliding 1-hour window). Safe before/after init().
 */
export function getWorkingSessionID(): string {
  const workingSessionID = ensureWorkingSessionID();
  state.workingSessionID = workingSessionID;
  return workingSessionID;
}

/** Bumps lastActivityAt to now, extending the sliding working-session window. */
export function touchWorkingSessionActivity(): void {
  state.workingSessionID = ensureWorkingSessionID();
}

export async function logout() {
  // A logout cannot abort an already-sent SDK request, but it must prevent that
  // request from mutating state or redirecting when it later completes.
  authActionEpoch += 1;
  resetStytchClient();
  userStore.clear();
  clearWorkingSession();
  const { data, error } = await runAuthSessionOperation(async () => {
    try {
      // Run this after any already-sent passwordLogin response so the logout
      // request (and its Set-Cookie response) is the final server mutation.
      return await manageCartwheelSession('logout');
    } finally {
      // A canceled login may have completed userStore.load() while it owned the
      // session-operation fence. Clear once more after that work settles.
      await userStore.clear();
      clearWorkingSession();
    }
  });
  if (error) {
    await handleGenericErrors(error, { suppressNotices: true });
  } else if (data) {
    state.authStatus = 'anonymous';
    state.userID = undefined;
    window.location.href = '/';
  }
}

export async function loginWithPassword(email: string, password: string) {
  if (email === '' || !emailRegex.test(email)) {
    noticesStore.error('Please enter a valid email address');
    return;
  }
  if (!password) {
    noticesStore.error('Please enter a password');
    return;
  }
  const actionEpoch = authActionEpoch;
  try {
    const stytchClient = await getStytchClient();
    if (!isCurrentAuthAction(actionEpoch)) return;
    const res = await stytchClient.passwords.authenticate({
      email,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    if (!isCurrentAuthAction(actionEpoch)) return;
    const isSignedIn = await runAuthSessionOperation(async () => {
      if (!isCurrentAuthAction(actionEpoch)) return false;
      const signedIn = await handleTokenAfterSignin(res.session_token);
      if (!isCurrentAuthAction(actionEpoch) || !signedIn) return false;
      await initializeAuthState();
      return isCurrentAuthAction(actionEpoch);
    });
    if (!isCurrentAuthAction(actionEpoch)) return;
    if (!isSignedIn) {
      // handleTokenAfterSignin already surfaced the reason. Redirecting would
      // navigate away from that notice without a session.
      return;
    }

    // Handle redirect after successful login
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirectTo');
    if (redirectTo) {
      window.location.href = buildRedirectUrl(redirectTo);
    } else {
      window.location.href = getDashboardUrl();
    }
  } catch (e) {
    if (!isCurrentAuthAction(actionEpoch) || isCancelledAuthAction(e)) return;
    noticesStore.error(getStytchErrorMessage(e));
  }
}

export async function signupWithPassword(
  firstname: string,
  lastname: string,
  email: string,
  password: string,
) {
  if (!firstname || !lastname) {
    noticesStore.error('Please enter first and last name');
    return;
  }
  if (!email || !emailRegex.test(email)) {
    noticesStore.error('Please enter a valid email address');
    return;
  }
  if (!password) {
    noticesStore.error('Please enter a password');
    return;
  }
  const actionEpoch = authActionEpoch;
  try {
    const stytchClient = await getStytchClient();
    if (!isCurrentAuthAction(actionEpoch)) return;
    const res = await stytchClient.passwords.create({
      email,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    if (!isCurrentAuthAction(actionEpoch)) return;
    await stytchClient.user.update({
      name: {
        first_name: firstname,
        last_name: lastname,
      },
    });
    if (!isCurrentAuthAction(actionEpoch)) return;
    const isSignedIn = await runAuthSessionOperation(async () => {
      if (!isCurrentAuthAction(actionEpoch)) return false;
      const signedIn = await handleTokenAfterSignin(res.session_token);
      return isCurrentAuthAction(actionEpoch) && signedIn;
    });
    if (!isCurrentAuthAction(actionEpoch)) return;
    if (isSignedIn) {
      // POST LAUNCH TODO: Figure out if we can change how this email looks so that it's more user friendly. JJ doesn't like the copy on it and the "Log in" button
      // await verifyEmail(email); // Send email verification
      return true;
    } else {
      // Occurs if users used a bad invite link
      await deleteUser(res.user_id);
    }
  } catch (error: unknown) {
    if (!isCurrentAuthAction(actionEpoch) || isCancelledAuthAction(error))
      return;
    console.log(error);
    if (error && typeof error === 'object' && 'error_type' in error) {
      const type = error.error_type as StytchAPIErrorType;
      if (type === 'duplicate_email') {
        // Special case with more explanation needed
        noticesStore.error(
          'Email is already registered. Redirecting you to login...',
          5000,
        );
        let path = '/login';
        path = appendLoginQueryParams(path);
        console.log(`Navigating to ${path}`);
        setTimeout(() => (window.location.href = path), 2500);
      } else {
        const message = StytchMessages[type] || 'Something went wrong.';
        noticesStore.error(message);
      }
    } else {
      noticesStore.error(getStytchErrorMessage(error));
    }
  }
}

export async function requestPasswordReset(email: string) {
  const { error } = await manageCartwheelSession('resetPassword', {
    email: encodeURIComponent(email),
  });
  if (error) {
    await handleGenericErrors(error);
  } else {
    noticesStore.info('Check your email for a reset link!');
  }
}

export async function resetPassword(password: string, token: string) {
  const actionEpoch = authActionEpoch;
  try {
    const stytchClient = await getStytchClient();
    if (!isCurrentAuthAction(actionEpoch)) return false;
    await stytchClient.passwords.resetByEmail({
      token,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    if (!isCurrentAuthAction(actionEpoch)) return false;
    noticesStore.info(
      'Password reset successfully! Please login with your new password.',
    );
    return true;
  } catch (error: unknown) {
    if (!isCurrentAuthAction(actionEpoch) || isCancelledAuthAction(error))
      return false;
    console.log(error);
    if (error && typeof error === 'object' && 'error_type' in error) {
      const message =
        StytchMessages[error.error_type as StytchAPIErrorType] ||
        'Something went wrong.';
      noticesStore.error(message);
    } else {
      noticesStore.error(getStytchErrorMessage(error));
    }
    return false;
  }
}

export async function verifyEmail(email: string) {
  const queryParams: Record<string, string> = {
    email: encodeURIComponent(email),
  };
  // if the user has a redirectTo param, we need to add it to the query params
  const params = new URLSearchParams(window.location.search);

  // INFO: Adding another encoding is intentional so that it is handled correctly passing
  // through Stytch's RedirectURL mechanism
  if (params.has('redirectTo')) {
    queryParams['redirectTo'] = encodeURIComponent(
      params.get('redirectTo') || '',
    );
  }
  const session = getSessionFromStytchCookie();
  if (session) {
    queryParams['token'] = session;
  }
  const { error } = await manageCartwheelSession('verifyEmail', queryParams);
  if (error) {
    await handleGenericErrors(error);
    return false;
  } else {
    return true;
  }
}

export type OAuthIntent = 'login' | 'signup';

export async function continueWithGoogle(options?: { intent?: OAuthIntent }) {
  const actionEpoch = authActionEpoch;
  try {
    const redirectUrl = getRedirectUrl(options?.intent);
    const stytchClient = await getStytchClient();
    if (!isCurrentAuthAction(actionEpoch)) return;
    await stytchClient.oauth.google.start({
      login_redirect_url: redirectUrl,
      signup_redirect_url: redirectUrl,
      custom_scopes: ['openid', 'email', 'profile'],
    });
  } catch (error: unknown) {
    if (!isCurrentAuthAction(actionEpoch) || isCancelledAuthAction(error))
      return;
    noticesStore.error(getStytchErrorMessage(error));
  }
}

export async function continueWithFacebook() {
  const actionEpoch = authActionEpoch;
  try {
    const stytchClient = await getStytchClient();
    if (!isCurrentAuthAction(actionEpoch)) return;
    await stytchClient.oauth.facebook.start({
      login_redirect_url: getRedirectUrl(),
      signup_redirect_url: getRedirectUrl(),
      custom_scopes: [],
    });
  } catch (error: unknown) {
    if (!isCurrentAuthAction(actionEpoch) || isCancelledAuthAction(error))
      return;
    noticesStore.error(getStytchErrorMessage(error));
  }
}

//// Helper functions:

/**
 * Build the full redirect URL based on the redirectTo parameter.
 * Mirrors the backend logic in lambdaLoginEntry.ts buildRedirectBase().
 *
 * Examples:
 * - "studio" → https://studio.getcartwheel.com or https://studio-staging.getcartwheel.com
 * - "apps/comic" → https://app.getcartwheel.com/comic
 * - "/checkout" → /checkout (same origin)
 */
export function buildRedirectUrl(redirectTo: string): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Determine environment from current hostname
  const isLocal = hostname.includes('local');
  const isStaging = hostname.includes('staging');

  // Get the disambiguator suffix for subdomains
  const disambiguator = isLocal ? '' : isStaging ? '-staging' : '';

  // If it's a relative path (starts with /), keep it on the same origin
  if (redirectTo.startsWith('/')) {
    return redirectTo;
  }

  // Build redirect base and path based on prefix
  let baseUrl: string;
  let path: string;

  if (redirectTo.startsWith('studio')) {
    if (isLocal) {
      baseUrl = `${protocol}//local.getcartwheel.com:3001`;
    } else {
      baseUrl = `https://studio${disambiguator}.getcartwheel.com`;
    }
    // Remove 'studio' prefix from path, e.g., 'studio/project' → '/project'
    path = redirectTo.replace(/^studio\/?/, '/');
  } else if (redirectTo.startsWith('labeler')) {
    if (isLocal) {
      baseUrl = `${protocol}//local.getcartwheel.com:3010`;
    } else {
      baseUrl = `https://labeler${disambiguator}.getcartwheel.com`;
    }
    path = redirectTo.replace(/^labeler\/?/, '/');
  } else if (redirectTo.startsWith('apps')) {
    if (isLocal) {
      baseUrl = `${protocol}//local.getcartwheel.com:3010`;
    } else {
      baseUrl = `https://app${disambiguator}.getcartwheel.com`;
    }
    // Remove 'apps' prefix from path, e.g., 'apps/comic' → '/comic'
    path = redirectTo.replace(/^apps\/?/, '/');
  } else {
    // Default: stay on the main site
    if (isLocal) {
      baseUrl = `${protocol}//local.getcartwheel.com:3000`;
    } else if (isStaging) {
      baseUrl = 'https://staging.getcartwheel.com';
    } else {
      baseUrl = 'https://getcartwheel.com';
    }
    path = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
  }

  // Ensure path starts with /
  if (path && !path.startsWith('/')) {
    path = `/${path}`;
  }

  return `${baseUrl}${path}`;
}

async function deleteStytchCookies() {
  document.cookie = `stytch_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `stytch_session_jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

async function deleteUser(userID: string) {
  deleteStytchCookies();
  const { error } = await manageCartwheelSession('deleteUser', {
    userID,
  });
  if (error) {
    await handleGenericErrors(error);
  }
}

function getStytchErrorMessage(error: unknown) {
  if (error && typeof error === 'object' && 'error_type' in error) {
    const type = error.error_type as StytchAPIErrorType;
    if (StytchMessages[type]) return StytchMessages[type];
  }
  if (error && typeof error === 'object' && 'error_message' in error) {
    const message = error.error_message;
    if (typeof message === 'string') return message;
  }
  return 'Something went wrong.';
}

async function handleTokenAfterSignin(token: string) {
  const params = new URLSearchParams(window.location.search);
  let queryParams = `?token=${token}`;
  if (params.has('invite')) {
    queryParams += `&invite=${params.get('invite')}`;
  }
  if (params.has('redirectTo')) {
    queryParams += `&redirectTo=${params.get('redirectTo')}`;
  }
  // Password login/signup calls the auth API directly, so the tenant from the
  // login host has to travel with the request the same way it does on OAuth.
  const clientSlug = getCurrentClientSlug();
  if (clientSlug) {
    queryParams += `&client=${clientSlug}`;
  }

  const { data, error } = await manageCartwheelSession(
    'passwordLogin',
    queryParams,
  );
  if (error) {
    await handleGenericErrors(error);
    return false;
  } else if (data) {
    return true;
  }
  return false;
}

function getRedirectUrl(intent?: OAuthIntent) {
  let redirectURL = getApiDomain('auth');
  redirectURL = appendLoginQueryParams(redirectURL, intent);

  return redirectURL;
}

function appendLoginQueryParams(path: string, intent?: OAuthIntent) {
  let url = path.slice(0);
  const params = new URLSearchParams(window.location.search);
  const redirectTo =
    params.get('redirectTo') ||
    (intent === 'login' ? null : NEW_USER_REDIRECT_TO);

  // INFO: Adding another encoding is intentional so that it is handled correctly passing
  // through Stytch's RedirectURL mechanism
  if (redirectTo) {
    url += `${
      url.includes('?') ? '&' : '?'
    }redirectTo=${encodeURIComponent(redirectTo)}`;
  }
  if (params.has('invite')) {
    url += `${url.includes('?') ? '&' : '?'}invite=${params.get('invite')}`;
  }
  // Stytch does not send intent. Login-page Google OAuth must set it on both
  // redirect URLs so a first-time Google user is not treated as a signup.
  if (intent === 'login') {
    url += `${url.includes('?') ? '&' : '?'}intent=login`;
  }
  // A dedicated login host (acme.getcartwheel.com) is the only hint of which
  // tenant a first session belongs to, and Stytch redirects leave that host.
  url = appendClientQueryParam(url);
  return url;
}

function getInitialStatus(): 'anonymous' | 'user' {
  const userID = getUserIDFromCookie();
  if (userID && userID !== ANONYMOUS_USER_ID) {
    return 'user';
  } else {
    return 'anonymous';
  }
}

function getUserIDFromCookie() {
  if (typeof document === 'undefined') return undefined;
  const suffix = getCookieSuffix();
  const cookies = document.cookie.split('; ');
  const cartwheelUserCookie = cookies.find((cookie) =>
    cookie.startsWith(`cartwheelUserID${suffix}=`),
  );
  if (cartwheelUserCookie) {
    const userID = cartwheelUserCookie.split('=')[1];
    return userID;
  }
}

function getSessionFromStytchCookie() {
  const cookies = document.cookie.split('; ');
  const stytchSessionCookie = cookies.find((cookie) =>
    cookie.startsWith('stytch_session'),
  );
  if (stytchSessionCookie) {
    const session = stytchSessionCookie.split('=')[1];
    return session;
  }
}

function generateWorkingSessionID() {
  return `ses-${ulid()}`;
}

function readStoredWorkingSession(): StoredWorkingSession | undefined {
  try {
    const raw = localStorage.getItem(WORKING_SESSION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<StoredWorkingSession>;
    if (
      typeof parsed.workingSessionID !== 'string' ||
      parsed.workingSessionID.length === 0 ||
      typeof parsed.lastActivityAt !== 'number'
    ) {
      return undefined;
    }
    return {
      workingSessionID: parsed.workingSessionID,
      lastActivityAt: parsed.lastActivityAt,
    };
  } catch {
    return undefined;
  }
}

function writeStoredWorkingSession(session: StoredWorkingSession) {
  localStorage.setItem(WORKING_SESSION_STORAGE_KEY, JSON.stringify(session));
}

function clearWorkingSession() {
  localStorage.removeItem(WORKING_SESSION_STORAGE_KEY);
  state.workingSessionID = undefined;
}

/**
 * Returns a valid metrics workingSessionID. Reuses the stored ID when still
 * within WORKING_SESSION_TTL_MS and bumps lastActivityAt; otherwise mints a
 * new ses-{ulid}.
 */
function ensureWorkingSessionID(): string {
  const now = Date.now();
  const existing = readStoredWorkingSession();
  if (existing && now - existing.lastActivityAt < WORKING_SESSION_TTL_MS) {
    writeStoredWorkingSession({
      workingSessionID: existing.workingSessionID,
      lastActivityAt: now,
    });
    return existing.workingSessionID;
  }

  const session: StoredWorkingSession = {
    workingSessionID: generateWorkingSessionID(),
    lastActivityAt: now,
  };
  writeStoredWorkingSession(session);
  return session.workingSessionID;
}
